import { ReactNode, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Config } from 'final-form';

import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { Typography, LoadingButton, SizingMultiplier } from '@ocx/ui';
import { SignupSupportLink } from '@ocx/feat-support';
import { PhoneNumberField } from '@ocx/ui-forms';

import { ReferralCodeManualEntryButton } from './components/referral-code-manual-entry-button.component';
import { ReferralCodeAppliedAlert } from './components/referral-code-applied-alert.component';
import { SignupPhoneNumberFormFields } from './signup-phone-number-form.types';
import { Disclaimer } from './disclaimer.component';
import { validation } from './signup-phone-number-form.validation';
import { messages } from '../../signup.messages';

const signupMessageComponents = {
  strong: (chunks: ReactNode) => (
    <Typography component="span" variant="button">
      {chunks}
    </Typography>
  ),
};

type SignupPhoneNumberFormControllerProps = {
  phoneNumber: string;
  referralCode: string | null;
  isReferralCodeManualEntryEnabled: boolean;
  onReferralProgramManualEntryClick: () => void;
  onSubmit(values: SignupPhoneNumberFormFields): Promise<void>;
  termsOfServiceLink: string;
  privacyPolicyLink: string;
  optOutUrl?: string;
};

export const SignupPhoneNumberForm = (props: SignupPhoneNumberFormControllerProps) => {
  const intl = useIntl();
  const {
    phoneNumber,
    onSubmit,
    termsOfServiceLink,
    privacyPolicyLink,
    referralCode,
    isReferralCodeManualEntryEnabled,
    onReferralProgramManualEntryClick,
    optOutUrl,
  } = props;

  const initialValues = useMemo<SignupPhoneNumberFormFields>(
    () => ({ phoneNumber, referralCode }),
    [phoneNumber, referralCode],
  );

  const handleSubmit = useCallback<Config<SignupPhoneNumberFormFields>['onSubmit']>(
    (values) => onSubmit(values),
    [onSubmit],
  );

  return (
    <Box display="flex" flexDirection="column" flex={1} padding={SizingMultiplier.lg}>
      <Stack alignItems="stretch" spacing={SizingMultiplier.lg} mb={SizingMultiplier.lg}>
        <Typography variant="h4" align="center">
          <FormattedMessage {...messages['signup:page-title']} />
        </Typography>
        <Typography color="textSecondary" align="center">
          <FormattedMessage {...messages['signup:page-description']} values={signupMessageComponents} />
        </Typography>

        <Form<SignupPhoneNumberFormFields>
          initialValues={initialValues}
          validate={validation}
          onSubmit={handleSubmit}
          render={(form) => (
            <form onSubmit={form.handleSubmit}>
              <Stack spacing={SizingMultiplier.md} useFlexGap>
                <PhoneNumberField
                  inputProps={{
                    'data-testid': 'phone-number-field',
                  }}
                  variant="outlined"
                  disabled={form.submitting}
                  fullWidth
                  name="phoneNumber"
                  label={intl.formatMessage(messages['signup:phone-number-label'])}
                />
                <LoadingButton
                  type="submit"
                  loading={form.submitting}
                  disabled={form.hasValidationErrors || form.submitting}
                  data-testid="confirm-btn"
                  fullWidth>
                  <FormattedMessage {...buttonTextMessages['button-text:submit']} />
                </LoadingButton>
                {isReferralCodeManualEntryEnabled && !referralCode && (
                  <ReferralCodeManualEntryButton onClick={onReferralProgramManualEntryClick} />
                )}
                {!!referralCode && <ReferralCodeAppliedAlert />}
                <SignupSupportLink />
              </Stack>
            </form>
          )}
        />
      </Stack>
      <Box mt={SizingMultiplier.xl}>
        <Disclaimer
          termsOfServiceLink={termsOfServiceLink}
          privacyPolicyLink={privacyPolicyLink}
          optOutUrl={optOutUrl}
        />
      </Box>
    </Box>
  );
};
